/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2026 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { ErrorBoundary } from '@umati/dashboard-common';
import { IMachine } from '@umati/dashboard-types';
import FallbackDetail from 'components/detail/common/FallbackDetail';
import ScaleDetail from 'components/detail/scale/ScaleDetail';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import type { StoreType } from 'redux/store';
import MqttTopics from 'utilities/MqttTopics';
import OpcUaMqttPubSub from 'utilities/PubSub/OpcUaMqttPubSub';
import SubscriptionComponent from 'utilities/SubscriptionComponent';
import SurfaceTechnologyDetail from 'views/SurfaceTechnologyDetail';
import TemplateTypeDetail from 'views/TemplateTypeDetail';
import TemplateTypeDetailPubSub from 'views/TemplateTypeDetailPubSub';
import TemplateTypeDetailPubSubVerbose from 'views/TemplateTypeDetailPubSubVerbose';

import Footer from './footer/footer';

interface ISubscribedDetailState {
  mqttData: any;
}

type ISubscribedDetailParams = {
  machine: string;
  fairTag?: string;
  typeId: string;
};

const SubscribedDetailByTypeDefiniton = {
  SurfaceTechnologyCoatingSystemType: SurfaceTechnologyDetail,
  SurfaceTechnologyObjectType: SurfaceTechnologyDetail,
  scale: ScaleDetail,
};

// type Encoding = 1 | 2 | 3;

// URL: localhost:30003/all/MachineToolType/clientId%3Disw%7Cgateway%3Bnsu%3Dhttp_3A_2F_2Fexample.com_2FShowcaseMachineTool_2F%3Bi%3D66382
// biome-ignore lint/correctness/noUnusedVariables: Was already ignored  in ESLint
const mockMachine: IMachine = {
  Id: 263,
  TypeDefinition: 'MachineToolType',
  MachineId: 'clientId=isw|gateway;nsu=http_3A_2F_2Fexample.com_2FShowcaseMachineTool_2F;i=66382',
  ParentId: 'nsu=http:_2F_2Fopcfoundation.org_2FUA_2FMachinery_2F;i=1001',
  ImagePath: 'test.png',
  Data: {
    SoftwareRevision: 'v1.0.0',
    Model: {
      text: 'ShowCaseMachineTool',
      locale: 'en',
    },
    Location: 'EMO 6 A18/Virtual 1 1/N 49.871215 E 8.654204',
    DeviceClass: 'Machining centre (other)',
    ProductCode: '2653837gg1548',
    Manufacturer: {
      text: 'umati Showcase',
      locale: 'en',
    },
    SerialNumber: '20240301-4711',
    ProductInstanceUri: 'https://umati.org/UA/instance/ShowCaseMachineTool-20240301-4711',
    YearOfConstruction: 2024,
  },
  ShortId: 'M263',
  MqttTopic: 'isw/gateway/BasicMachineTool',
  ClientId: 'isw|gateway',
  Online: true,
  Positions: {
    emo: {
      hall: '6',
      booth: 'A18',
    },
    virtual: {
      hall: '1',
      booth: '1',
    },
    offsite: {
      lat: 49.871215,
      lng: 8.654204,
    },
    offsiteClean: {
      lat: 'N 49.871215',
      lng: 'E 8.654204',
    },
  },
  Favorite: false,
  Encoding: 3,
};

export default function SubscribedDetail() {
  const params = useParams() as ISubscribedDetailParams;
  const [searchParams] = useSearchParams();
  const typeId = params.typeId;
  const DetailComponentV2 = SubscribedDetailByTypeDefiniton[typeId] || TemplateTypeDetail;
  const DetailComponentV3 = TemplateTypeDetailPubSub;
  const DetailComponentV4 = TemplateTypeDetailPubSubVerbose;

  // const machineId = decodeURIComponent(params.machine).replace('_3A', ':'); // try to replace colon
  const machineId = decodeURIComponent(params.machine);
  const fairTag = params.fairTag;
  let topic = MqttTopics.Machine(typeId, machineId);
  // let encoding: Encoding = 2;

  const [{ mqttData }, setState] = useState<ISubscribedDetailState>({
    mqttData: undefined,
  });
  const [scrolledToTop, setScrolledToTop] = useState(false);

  const updateMachineViaMqtt = (data, topicPar) => {
    if (topicPar === topic) {
      setState({ mqttData: data });
    } else {
      console.log(`Topic not match: ${topic} ${topicPar}`);
    }
  };

  useEffect(() => {
    if (!scrolledToTop) {
      window.scrollTo(0, 0);
    }
    setScrolledToTop(true);
    setState({ mqttData: undefined });
  }, [topic, scrolledToTop]);

  const machines = useSelector((state: StoreType) => state.machines.Machines);
  const expectedEncoding = searchParams.get('v');

  const machine = useMemo(() => {
    // machines.push(mockMachine); // TODO: Remove this line, only for testing without backend
    // if (expectedEncoding === null) {
    return machines.find((m) => m.MachineId === machineId); // try to find with replaced colon
    // } else {
    //   return machines.find(m => m.MachineId === machineId && (expectedEncoding === '2' && (m.Encoding === undefined || m.Encoding === 2) || expectedEncoding === '3' && m.Encoding === 3));
    // }
  }, [machines, machineId]);

  if (machine?.Encoding === 3) {
    // encoding = 3;
    topic = machine.MqttTopic || '';
  }

  return (
    <main className="footer-wrapper" style={{ scrollBehavior: 'auto' }}>
      {expectedEncoding == '2' && (
        <ErrorBoundary ErrorComponent={FallbackDetail} ErrorComponentProps={{ fairTag, machineId, typeId }}>
          <SubscriptionComponent topic={topic} subscriptionCallback={updateMachineViaMqtt}>
            <DetailComponentV2
              data={mqttData}
              fairTag={fairTag}
              machineId={machineId}
              typeId={typeId}
              machine={machine}
            />
          </SubscriptionComponent>
        </ErrorBoundary>
      )}
      {expectedEncoding == '3' && machine !== undefined && (
        <OpcUaMqttPubSub topicPath={topic}>
          <DetailComponentV3 fairTag={fairTag} machineId={machineId} typeId={typeId} machine={machine} />
        </OpcUaMqttPubSub>
      )}
      {expectedEncoding == '4' && machine !== undefined && (
        <OpcUaMqttPubSub topicPath={topic}>
          <DetailComponentV4 fairTag={fairTag} machineId={machineId} typeId={typeId} machine={machine} />
        </OpcUaMqttPubSub>
      )}
      {machine === undefined && (
        <p style={{ display: 'flex', justifySelf: 'center', paddingTop: '20px' }}>
          Machine {machineId} is not in the database.
        </p>
      )}
      <div className="container-max">
        <Footer />
      </div>
    </main>
  );
}
